'use client';

import dashboardStyles from '@/app/dashboard/page.styles';
import styles from '@/app/orders/components/Filters.style';

import { DeliveryPlanType } from '@/core/entities/delivery-plan-type';
import { Store } from '@/core/entities/store';

import {
  AppColors,
  Button,
  DateRange,
  futureDatePresets,
  pastDatePresets
} from '@cmbg-traky-web/design-system';
import { Alert, Checkbox, Col, Form, Grid, Input, Row, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayjsInstance } from '@/core/utils/date.util';
import { sortStoresByCode } from '@/core/utils/sort.util';
import { formatSearchTerm, processPurchaseOrderValue } from '@/core/utils/string.util';

import DateRangeGlobalStyles from './DateRangeGlobalStyles';

type FormInstance = ReturnType<typeof Form.useForm>[0];
type SelectOption = { label: string; value: string };

export type FiltersProps = Readonly<{
  form: FormInstance;
  stores: Store[];
  deliveryPlanTypes: DeliveryPlanType[];

  purchaseOrder: string | undefined;
  onPurchaseOrderChange: (value: string | undefined) => void;

  creditAccount: string | undefined;
  onCreditAccountChange: (value: string | undefined) => void;

  invoiceDate:
    | [ReturnType<typeof dayjsInstance> | null, ReturnType<typeof dayjsInstance> | null]
    | undefined;
  onInvoiceDateChange: (
    dates: (ReturnType<typeof dayjsInstance> | null)[] | null,
    dateStrings: [string, string] | null
  ) => void;

  promiseDate:
    | [ReturnType<typeof dayjsInstance> | null, ReturnType<typeof dayjsInstance> | null]
    | undefined;
  onPromiseDateChange: (
    dates: (ReturnType<typeof dayjsInstance> | null)[] | null,
    dateStrings: [string, string] | null
  ) => void;

  selectedStores: string[];
  onSelectedStoresChange: (values: string[]) => void;

  deliveryPlanType: string[] | undefined;
  onDeliveryPlanTypeChange: (values: string[]) => void;

  onClearFilters: () => void;
  onSearch: () => void;
  onApply?: () => void;
  creditAccountError?: boolean;
  onFiltersChange?: (filtersData: {
    purchaseOrder?: string;
    creditAccount?: string;
    invoiceDate?: [
      ReturnType<typeof dayjsInstance> | null,
      ReturnType<typeof dayjsInstance> | null
    ];
    promiseDate?: [
      ReturnType<typeof dayjsInstance> | null,
      ReturnType<typeof dayjsInstance> | null
    ];
    deliveryPlanType?: string[];
    selectedStores?: string[];
  }) => void;
}>;

/**
 * Filters component for advanced search.
 *
 * @param {Omit<FiltersProps, 'purchaseOrder' | 'onPurchaseOrderChange' | 'creditAccount' | 'onCreditAccountChange' | 'invoiceDate' | 'onInvoiceDateChange' | 'promiseDate' | 'onPromiseDateChange' | 'selectedStores' | 'onSelectedStoresChange'>} props - The component props.
 *
 * @returns {JSX.Element} The rendered component.
 */
export function Filters({
  form,
  stores,
  deliveryPlanTypes,
  onClearFilters,
  onSearch,
  onApply,
  creditAccountError = false,
  onFiltersChange
}: Omit<
  FiltersProps,
  | 'purchaseOrder'
  | 'onPurchaseOrderChange'
  | 'creditAccount'
  | 'onCreditAccountChange'
  | 'invoiceDate'
  | 'onInvoiceDateChange'
  | 'promiseDate'
  | 'onPromiseDateChange'
  | 'selectedStores'
  | 'onSelectedStoresChange'
  | 'deliveryPlanType'
  | 'onDeliveryPlanTypeChange'
>) {
  const { t } = useTranslation();
  const [purchaseOrder, setPurchaseOrder] = useState<string | undefined>(undefined);
  const [creditAccount, setCreditAccount] = useState<string | undefined>(undefined);
  const [invoiceDate, setInvoiceDate] = useState<
    [ReturnType<typeof dayjsInstance> | null, ReturnType<typeof dayjsInstance> | null] | undefined
  >(undefined);
  const [promiseDate, setPromiseDate] = useState<
    [ReturnType<typeof dayjsInstance> | null, ReturnType<typeof dayjsInstance> | null] | undefined
  >(undefined);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [deliveryPlanType, setDeliveryPlanType] = useState<string[] | undefined>(undefined);
  const [dateRangeWarningActive, setDateRangeWarningActive] = useState<boolean>(false);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  useEffect(() => {
    const formValues = form.getFieldsValue() as {
      purchaseOrder?: string;
      creditAccount?: string;
      invoiceDate?: [
        ReturnType<typeof dayjsInstance> | null,
        ReturnType<typeof dayjsInstance> | null
      ];
      promiseDate?: [
        ReturnType<typeof dayjsInstance> | null,
        ReturnType<typeof dayjsInstance> | null
      ];
      deliveryPlanType?: string[];
      stores?: string[];
    };

    if (formValues.purchaseOrder !== undefined) {
      setPurchaseOrder(formValues.purchaseOrder);
    }
    if (formValues.creditAccount !== undefined) {
      setCreditAccount(formValues.creditAccount);
    }
    if (formValues.invoiceDate !== undefined) {
      setInvoiceDate(formValues.invoiceDate);
    }
    if (formValues.promiseDate !== undefined) {
      setPromiseDate(formValues.promiseDate);
    }
    if (formValues.deliveryPlanType !== undefined) {
      setDeliveryPlanType(formValues.deliveryPlanType);
    }
    if (formValues.stores !== undefined) {
      setSelectedStores(formValues.stores);
    }
  }, [form]);

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        purchaseOrder,
        creditAccount,
        invoiceDate,
        promiseDate,
        deliveryPlanType,
        selectedStores
      });
    }
  }, [
    purchaseOrder,
    creditAccount,
    invoiceDate,
    promiseDate,
    deliveryPlanType,
    selectedStores,
    onFiltersChange
  ]);

  useEffect(() => {
    const isRangeTooWide = (
      range?: [ReturnType<typeof dayjsInstance> | null, ReturnType<typeof dayjsInstance> | null]
    ) => {
      if (!range) return false;
      const [start, end] = range;
      if (!start || !end) return false;
      const diffDays = end.diff(start, 'day');
      return diffDays > 32;
    };

    const wideInvoice = isRangeTooWide(invoiceDate);
    const widePromise = isRangeTooWide(promiseDate);
    const hasStore = selectedStores && selectedStores.length > 0;
    const hasInvoiceDate = !!(invoiceDate && invoiceDate.filter(Boolean).length === 2);
    const hasPromiseDate = !!(promiseDate && promiseDate.filter(Boolean).length === 2);
    const hasAnyDate = hasInvoiceDate || hasPromiseDate;

    const storeWithoutDate = hasStore && !hasAnyDate;
    const dateWithoutStore = hasAnyDate && !hasStore;

    setDateRangeWarningActive(wideInvoice || widePromise || storeWithoutDate || dateWithoutStore);
  }, [purchaseOrder, creditAccount, deliveryPlanType, selectedStores, invoiceDate, promiseDate]);

  const storeOptions = useMemo(
    () =>
      stores
        .slice()
        .sort(sortStoresByCode)
        .map((s) => ({
          label: s.organizationId ? `${s.organizationId.padStart(2, '0')} – ${s.name}` : s.name,
          value: s.organizationId
        })),
    [stores]
  );

  const deliveryPlanOptions = useMemo(
    () =>
      deliveryPlanTypes.map((dpt) => {
        const id = dpt.id || '';
        const name = dpt.name || '';
        const planNameMap: Record<string, string> = {
          EA: 'Entrega Acarreo',
          EB: 'Entrega Bodega',
          PCE: 'Pago Contra Entrega',
          PE: 'Pendiente de Entrega',
          ED: 'Entrega Diferida',
          DI: 'Despacho Inmediato',
          EDP: 'Entrega Directa Proveedor',
          IP: 'Inventario Proveedor'
        };

        const displayName = planNameMap[id] || name;

        return {
          label: `${id} - ${displayName}`,
          value: id
        };
      }),
    [deliveryPlanTypes]
  );

  const filterSelectOption = (input: string, option?: SelectOption) => {
    if (!option) return false;
    const searchValue = input.trim().toLowerCase();
    const label = String(option.label || '').toLowerCase();
    const value = String(option.value || '').toLowerCase();

    return label.includes(searchValue) || value.includes(searchValue);
  };

  const renderStoreTag = (props: {
    label: React.ReactNode;
    value: string;
    closable: boolean;
    onClose: () => void;
  }) => {
    const { label, value, closable, onClose } = props;
    const store = stores.find((s) => s.organizationId === value);
    const displayLabel = store?.organizationId ? `${store.organizationId.padStart(2, '0')}` : label;

    return (
      <span style={styles.storeTag} onClick={(e) => e.stopPropagation()}>
        {displayLabel}
        {closable && (
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            style={styles.storeTagClose}>
            ×
          </span>
        )}
      </span>
    );
  };

  const isSearchButtonDisabled = useMemo(() => {
    const hasAnyFieldFilled =
      !!purchaseOrder ||
      !!creditAccount ||
      (invoiceDate && invoiceDate.filter(Boolean).length === 2) ||
      (promiseDate && promiseDate.filter(Boolean).length === 2) ||
      (deliveryPlanType && deliveryPlanType.length > 0) ||
      (selectedStores && selectedStores.length > 0);

    return !hasAnyFieldFilled || dateRangeWarningActive;
  }, [
    purchaseOrder,
    creditAccount,
    invoiceDate,
    promiseDate,
    deliveryPlanType,
    selectedStores,
    dateRangeWarningActive
  ]);

  const isClearFiltersButtonDisabled = useMemo(() => {
    return (
      !purchaseOrder &&
      !creditAccount &&
      (!invoiceDate || invoiceDate.filter(Boolean).length === 0) &&
      (!promiseDate || promiseDate.filter(Boolean).length === 0) &&
      (!deliveryPlanType || deliveryPlanType.length === 0) &&
      (!selectedStores || selectedStores.length === 0)
    );
  }, [purchaseOrder, creditAccount, invoiceDate, promiseDate, deliveryPlanType, selectedStores]);

  return (
    <>
      <DateRangeGlobalStyles />
      <div style={styles.advancedOverlay}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item label='Nº orden de compra de cliente' name='purchaseOrder'>
              <Input
                allowClear
                size='large'
                value={purchaseOrder}
                onChange={(e) => {
                  const value = processPurchaseOrderValue(e.target.value);
                  setPurchaseOrder(value);
                  form.setFieldsValue({ purchaseOrder: value });
                }}
                onBlur={(e) => {
                  const value = processPurchaseOrderValue(e.target.value);
                  setPurchaseOrder(value);
                  form.setFieldsValue({ purchaseOrder: value });
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label='Cuenta de crédito de cliente'
              name='creditAccount'
              validateStatus={creditAccountError ? 'error' : ''}
              help={creditAccountError ? t('errors:creditAccountNotFound') : ''}>
              <Input
                allowClear
                size='large'
                value={creditAccount}
                onChange={(e) => {
                  const value = formatSearchTerm(e.target.value);
                  setCreditAccount(value);
                  form.setFieldsValue({ creditAccount: value });
                }}
                onBlur={(e) => {
                  const value = formatSearchTerm(e.target.value);
                  setCreditAccount(value);
                  form.setFieldsValue({ creditAccount: value });
                }}
                status={creditAccountError ? 'error' : undefined}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            {isMobile ? (
              <div className='mobile-date-range-container' style={styles.mobileDateRangeContainer}>
                <DateRange
                  label='Fecha de facturación'
                  name='invoiceDate'
                  value={invoiceDate}
                  onChange={(dates) => {
                    setInvoiceDate(
                      dates as
                        | [
                            ReturnType<typeof dayjsInstance> | null,
                            ReturnType<typeof dayjsInstance> | null
                          ]
                        | undefined
                    );
                  }}
                  customPresets={pastDatePresets}
                />
                <DateRange
                  label='Fecha promesa de entrega'
                  name='promiseDate'
                  value={promiseDate}
                  onChange={(dates) => {
                    setPromiseDate(
                      dates as
                        | [
                            ReturnType<typeof dayjsInstance> | null,
                            ReturnType<typeof dayjsInstance> | null
                          ]
                        | undefined
                    );
                  }}
                  customPresets={futureDatePresets}
                />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <DateRange
                    label='Fecha de facturación'
                    name='invoiceDate'
                    value={invoiceDate}
                    onChange={(dates) => {
                      setInvoiceDate(
                        dates as
                          | [
                              ReturnType<typeof dayjsInstance> | null,
                              ReturnType<typeof dayjsInstance> | null
                            ]
                          | undefined
                      );
                    }}
                    customPresets={pastDatePresets}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <DateRange
                    label='Fecha promesa de entrega'
                    name='promiseDate'
                    value={promiseDate}
                    onChange={(dates) => {
                      setPromiseDate(
                        dates as
                          | [
                              ReturnType<typeof dayjsInstance> | null,
                              ReturnType<typeof dayjsInstance> | null
                            ]
                          | undefined
                      );
                    }}
                    customPresets={futureDatePresets}
                  />
                </Col>
              </Row>
            )}
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label='Tienda de entrega' name='stores'>
              <Select
                showSearch
                size='large'
                mode='multiple'
                allowClear
                placeholder={t('placeholders:stores')}
                options={storeOptions}
                optionFilterProp='label'
                filterOption={(input, option) => filterSelectOption(input, option as SelectOption)}
                value={selectedStores}
                onChange={(values) => {
                  setSelectedStores(values);
                }}
                tagRender={renderStoreTag}
                menuItemSelectedIcon={null}
                optionRender={(option) => (
                  <div
                    style={
                      selectedStores.includes(option.value as string)
                        ? styles.optionContainerSelected
                        : styles.optionContainer
                    }>
                    <Checkbox
                      checked={selectedStores.includes(option.value as string)}
                      style={
                        selectedStores.includes(option.value as string)
                          ? styles.optionCheckboxSelected
                          : styles.optionCheckbox
                      }
                      onChange={() => {}}
                    />
                    <span>{option.label}</span>
                  </div>
                )}
                style={styles.selectStores}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item label='Plan de entrega' name='deliveryPlanType'>
              <Select
                showSearch
                size='large'
                mode='multiple'
                allowClear
                placeholder='Seleccione planes de entrega'
                options={deliveryPlanOptions}
                optionFilterProp='label'
                filterOption={(input, option) => filterSelectOption(input, option as SelectOption)}
                value={deliveryPlanType}
                onChange={(values) => {
                  setDeliveryPlanType(values);
                }}
                tagRender={(props) => {
                  const option = deliveryPlanOptions.find((opt) => opt.value === props.value);
                  const abbreviation = option?.value || String(props.label);
                  return (
                    <span
                      className='ant-tag ant-tag-blue'
                      style={dashboardStyles.deliveryPlanTag}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}>
                      {abbreviation}
                      {props.closable && (
                        <span
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onClose(e);
                          }}
                          style={dashboardStyles.deliveryPlanCloseIcon}
                          onMouseEnter={(e) => ((e.target as HTMLElement).style.opacity = '1')}
                          onMouseLeave={(e) => ((e.target as HTMLElement).style.opacity = '0.8')}>
                          ×
                        </span>
                      )}
                    </span>
                  );
                }}
                menuItemSelectedIcon={null}
                optionRender={(option) => (
                  <div
                    style={
                      deliveryPlanType?.includes(option.value as string)
                        ? dashboardStyles.optionContainerSelected
                        : dashboardStyles.optionContainer
                    }>
                    <Checkbox
                      checked={deliveryPlanType?.includes(option.value as string)}
                      style={
                        deliveryPlanType?.includes(option.value as string)
                          ? dashboardStyles.optionCheckboxSelected
                          : dashboardStyles.optionCheckbox
                      }
                      onChange={() => {}}
                    />
                    <span>{option.label}</span>
                  </div>
                )}
                style={{
                  ...styles.selectStores,
                  height: 'auto',
                  minHeight: '42px',
                  whiteSpace: 'normal'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {dateRangeWarningActive && (
          <Alert
            type='warning'
            showIcon
            banner
            message='Por favor, aplica un rango de fechas menor a 30 días y selecciona al menos una tienda de entrega para optimizar tu búsqueda.'
            style={{ margin: '8px 0' }}
          />
        )}

        <div style={styles.advancedActions}>
          <Button
            style={{
              ...styles.button__clear,
              color: isClearFiltersButtonDisabled
                ? AppColors.VA.NEUTRALS._40_
                : AppColors.VA.PRIMARY._50_,
              cursor: isClearFiltersButtonDisabled ? 'not-allowed' : 'pointer',
              opacity: isClearFiltersButtonDisabled ? 0.7 : 1
            }}
            type='link'
            onClick={() => {
              setPurchaseOrder(undefined);
              setCreditAccount(undefined);
              setInvoiceDate(undefined);
              setPromiseDate(undefined);
              setDeliveryPlanType([]);
              setSelectedStores([]);
              form.setFieldsValue({
                purchaseOrder: undefined,
                creditAccount: undefined,
                invoiceDate: undefined,
                promiseDate: undefined,
                deliveryPlanType: [],
                stores: []
              });
              onClearFilters();
            }}
            disabled={isClearFiltersButtonDisabled}>
            <span className='font-semibold'>{t('common:filters.clear')}</span>
          </Button>
          <Button
            style={styles.button__search}
            type='primary'
            onClick={() => {
              onSearch();
              if (onApply) {
                onApply();
              }
            }}
            disabled={isSearchButtonDisabled}>
            <span className='font-semibold'>{t('common:filters.apply')}</span>
          </Button>
        </div>
      </div>
    </>
  );
}
